import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getProjectById, updateProjectDetailsByEndpoint, uploadProjectDetailsAttachments, deleteProjectDetailsAttachment, getProjectTabs, getProjectSummaryAnalytics, getProjectTeamActivity } from '@services/projects.service';
import { projectsListPath, projectChatPath } from '@hooks/projects/projectHelpers';
import { invalidateProjectQueries } from '@hooks/projects/projectQueryInvalidation';
import { getRealtimeSocket, subscribeProject, unsubscribeProject } from '@services/realtime.socket';
import { PROJECT_DETAILS_UPDATE } from '@/ability';
import { openSSE } from '@utils/sse';

export const PROJECT_DETAIL_QUERY_KEY = (id) => ['projects', 'detail', id];
export const PROJECT_SUMMARY_ANALYTICS_QUERY_KEY = (id) => ['projects', 'summary-analytics', id];

/** Tabs removed from the product; backend may still send them — hide in UI and routing. */
export const REMOVED_PROJECT_TAB_IDS = new Set([
    'overview',
    'bugs',
    'contributors',
    'contribution',
]);

/** Collapse synonym tab ids so the sidebar does not show duplicate rows. */
function canonicalProjectTabId(tabId) {
    const id = String(tabId ?? '').trim().toLowerCase();
    if (id === 'projectdetail' || id === 'project-details' || id === 'project_details') return 'details';
    if (id === 'collaborator' || id === 'collaborators') return 'collaboration';
    return id;
}

export function filterProjectTabs(tabs) {
    if (!Array.isArray(tabs)) return [];
    const seen = new Set();
    const out = [];
    for (const t of tabs) {
        if (t.visible === false) continue;
        const id = String(t.id ?? '').trim().toLowerCase();
        if (REMOVED_PROJECT_TAB_IDS.has(id)) continue;
        const canon = canonicalProjectTabId(id);
        if (seen.has(canon)) continue;
        seen.add(canon);
        out.push(canon !== id ? { ...t, id: canon } : t);
    }
    return out;
}

export const useProjectDetail = (projectId, options) => {
    const opts = options ?? {};
    return useQuery({
        queryKey: PROJECT_DETAIL_QUERY_KEY(projectId),
        queryFn: () => getProjectById(projectId),
        enabled: !!projectId && (opts.enabled ?? true),
        staleTime: opts.staleTime ?? Infinity,
        refetchOnMount: opts.refetchOnMount ?? false,
        refetchOnWindowFocus: opts.refetchOnWindowFocus ?? false,
        refetchOnReconnect: opts.refetchOnReconnect ?? false,
        placeholderData: keepPreviousData,
    });
};

export const useProjectSummaryAnalytics = (projectId, options) => {
    const opts = options ?? {};
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!projectId || opts.enabled === false) return;

        const path = `/projects/${projectId}/summary-analytics/live`;
        const eventSource = openSSE(path);

        const handleUpdate = (event) => {
            try {
                const parsed = JSON.parse(event.data);
                queryClient.setQueryData(PROJECT_SUMMARY_ANALYTICS_QUERY_KEY(projectId), parsed);
            } catch (err) {
                console.error('Failed to parse summary-analytics SSE event data', err);
            }
        };

        eventSource.addEventListener('summary-analytics', handleUpdate);

        return () => {
            eventSource.removeEventListener('summary-analytics', handleUpdate);
            eventSource.close();
        };
    }, [projectId, queryClient, opts.enabled]);

    return useQuery({
        queryKey: PROJECT_SUMMARY_ANALYTICS_QUERY_KEY(projectId),
        queryFn: () => getProjectSummaryAnalytics(projectId),
        enabled: !!projectId && (opts.enabled ?? true),
        staleTime: opts.staleTime ?? Infinity,
        refetchOnMount: opts.refetchOnMount ?? false,
        refetchOnWindowFocus: opts.refetchOnWindowFocus ?? false,
        refetchOnReconnect: opts.refetchOnReconnect ?? false,
        placeholderData: keepPreviousData,
    });
};

export const useProjectTeamActivity = (projectId, params = {}, options = {}) => {
    return useQuery({
        queryKey: ['projects', 'team-activity', projectId, params],
        queryFn: () => getProjectTeamActivity(projectId, params),
        enabled: !!projectId && (options.enabled ?? true),
        placeholderData: keepPreviousData,
        ...options,
    });
};

// =========================================================
// Project detail shell (routing + tab sync)
// =========================================================

/**
 * Resolves effective tab id from URL param and project tabs (backend response only).
 */
function getEffectiveTabId(tabParam, tabs) {
    if (tabParam && tabs.some((t) => t.id === tabParam)) return tabParam;
    return tabs[0]?.id || 'details';
}

/**
 * Hook for ProjectDetail shell: project data, tab URL sync, and navigation callbacks.
 * Page only consumes this and renders UI.
 */
export function useProjectDetailShell() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    /** isPending = no cached data yet; tab changes reuse cache so no full-page spinner. */
    const { data: project, isPending: projectLoading, error: projectError } = useProjectDetail(projectId);
    const tabs = filterProjectTabs(project?.tabs ?? []);
    const tabFromUrl = searchParams.get('tab') || 'details';
    const effectiveTabId = getEffectiveTabId(tabFromUrl, tabs);

    useEffect(() => {
        if (!projectId) return;

        const socket = getRealtimeSocket();
        if (!socket) return;

        subscribeProject(projectId);

        const handleTabsUpdated = (data) => {
            const targetId = data?.projectId ?? data?.project_id ?? data?.id;
            if (targetId && String(targetId) !== String(projectId)) return;

            // Perform a completely in-memory update with zero network requests!
            queryClient.setQueryData(PROJECT_DETAIL_QUERY_KEY(projectId), (oldProject) => {
                if (!oldProject) return oldProject;

                const payloadKeys = {
                    milestones: 'milestones',
                    sprints: 'sprints',
                    qa: 'qa',
                    blockers: 'blockers',
                    backlogs: 'backlogs',
                    'change-requests': 'changeRequests',
                    reviews: 'reviews',
                    'completion-requests': 'submissions',
                    collaboration: 'collaborators'
                };

                const updatedTabs = (oldProject.tabs ?? []).map(tab => {
                    const payloadKey = payloadKeys[tab.id];
                    if (payloadKey) {
                        const isEnabled = data[payloadKey] !== undefined;
                        return { ...tab, visible: isEnabled };
                    }
                    return tab;
                });

                return {
                    ...oldProject,
                    modules: {
                        ...oldProject.modules,
                        ...data.modules,
                    },
                    tabs: updatedTabs,
                };
            });

            // Trigger a background refetch to guarantee consistent permission/capability sync
            queryClient.invalidateQueries({ queryKey: PROJECT_DETAIL_QUERY_KEY(projectId) });
        };

        socket.on('project:tabs:updated', handleTabsUpdated);

        return () => {
            socket.off('project:tabs:updated', handleTabsUpdated);
            unsubscribeProject(projectId);
        };
    }, [projectId, queryClient]);

    useEffect(() => {
        if (tabs.length === 0) return;
        const hasTabParam = new URLSearchParams(location.search).has('tab');
        if (!hasTabParam) {
            setSearchParams({ tab: effectiveTabId }, { replace: true });
            return;
        }
        if (!tabs.some((t) => t.id === tabFromUrl)) {
            setSearchParams({ tab: effectiveTabId }, { replace: true });
        }
    }, [tabs, tabFromUrl, effectiveTabId, location.search, setSearchParams]);

    const backToProjectsPath = projectsListPath(location.pathname);
    const chatPath = project?.chatId ? projectChatPath(location.pathname, project.chatId) : null;

    const goToProjects = useCallback(() => {
        navigate(backToProjectsPath);
    }, [navigate, backToProjectsPath]);

    const goToChat = useCallback(() => {
        if (chatPath) navigate(chatPath);
    }, [navigate, chatPath]);

    return {
        projectId,
        project,
        projectLoading,
        projectError,
        effectiveTabId,
        goToProjects,
        goToChat,
        backToProjectsPath,
        showChatButton: !!project?.chatId,
    };
}

// =========================================================
// Project details update (PATCH + attachments)
// =========================================================

/**
 * Update project description/details via PATCH /projects/:id/details.
 * Uses multipart when attachmentUrls or files are provided.
 * Accepts:
 *   - { projectDetails: { content } } or { content }
 *   - { content, attachmentUrls: [{ type: 'link', url, title? }], files?: File[] }
 * Invalidates project detail query on success.
 * @param {string} projectId
 */
export function useProjectDetailsUpdate(projectId) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (body) => {
            const content = body?.content ?? body?.projectDetails?.content ?? null;
            const attachments = body?.attachments ?? body?.attachmentUrls ?? [];
            const files = body?.files ?? [];
            return updateProjectDetailsByEndpoint(projectId, {
                content: content ?? '',
                attachments,
                files,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROJECT_DETAIL_QUERY_KEY(projectId) });
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const uploadAttachmentsMutation = useMutation({
        mutationFn: (body) =>
            uploadProjectDetailsAttachments(projectId, {
                attachmentUrls: body?.attachmentUrls ?? [],
                files: body?.files ?? [],
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROJECT_DETAIL_QUERY_KEY(projectId) });
        },
    });

    return {
        updateProjectDetails: mutation.mutateAsync,
        updatePending: mutation.isPending,
        updateError: mutation.error,
        uploadAttachments: uploadAttachmentsMutation.mutate,
        uploadAttachmentsPending: uploadAttachmentsMutation.isPending,
    };
}

export { PROJECT_DETAILS_UPDATE };

/**
 * Directly delete a project or description attachment via DELETE /projects/:id/details/attachments.
 * Accepts either { id } (project_attachments table row) or { url } (description JSON attachment).
 * Invalidates the project detail cache on success.
 * @param {string} projectId
 */
export function useProjectDetailsAttachmentDelete(projectId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => deleteProjectDetailsAttachment(projectId, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROJECT_DETAIL_QUERY_KEY(projectId) });
            invalidateProjectQueries(queryClient, projectId);
        },
    });
}

export function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export function resolveProfileImageUrl(url, envApiUrl) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
    }
    const baseUrl = envApiUrl?.replace(/\/api\/v\d+\/?$/, '')?.replace(/\/$/, '') || '';
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
}

export function looksLikeMarkdownDisplay(text) {
    const s = String(text ?? '').trim();
    if (s.length < 2) return false;
    return (
        /^#{1,6}\s/m.test(s) ||
        /^ {0,3}[-*+]\s/m.test(s) ||
        /^ {0,3}\d+\.\s/m.test(s) ||
        /\*\*[^*\n]+\*\*/.test(s) ||
        /(^|\s)_[^_`\n]+_(\s|$)/.test(s) ||
        /`[^`\n]+`/.test(s) ||
        /```[\s\S]*```/.test(s) ||
        /\[.+\]\([^)\s]+\)/.test(s) ||
        /^ {0,3}>\s/m.test(s)
    );
}

export function getMemberRoleLabel(member) {
    if (!member || typeof member !== 'object') return '—';
    const value =
        (typeof member.role === 'string' || typeof member.role === 'number' ? member.role : undefined) ??
        member.role?.name ??
        member.roleName ??
        member.role?.roleName ??
        member.designation ??
        member.position ??
        member.jobTitle;
    const role = String(value || '').trim();
    if (!role) return '—';
    if (role.toLowerCase() === 'pm') return 'PM';
    return role
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function isAttachmentImage(att) {
    const mime = (att.type || att.mimeType || '').toLowerCase();
    const ref = att.url || att.href || att.fileUrl || '';
    const name = att.title ?? att.name ?? ref;
    return (
        mime.startsWith('image/') ||
        ref.startsWith('data:image/') ||
        /\.(jpe?g|png|gif|webp|bmp)$/i.test(name || ref || '')
    );
}

export function isAttachmentPdf(att) {
    const mime = (att.type || att.mimeType || '').toLowerCase();
    const ref = att.url || att.href || att.fileUrl || '';
    const name = att.title ?? att.name ?? ref;
    return mime === 'application/pdf' || /\.pdf$/i.test(name || ref || '');
}

export function isAttachmentVideo(att) {
    const mime = (att.type || att.mimeType || '').toLowerCase();
    const ref = att.url || att.href || att.fileUrl || '';
    const name = att.title ?? att.name ?? ref;
    return (
        mime.startsWith('video/') ||
        /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(name || ref || '')
    );
}

export function isAttachmentDoc(att) {
    const mime = (att.type || att.mimeType || '').toLowerCase();
    const ref = att.url || att.href || att.fileUrl || '';
    const name = att.title ?? att.name ?? ref;
    return (
        mime === 'application/msword' ||
        mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        /\.(doc|docx)$/i.test(name || ref || '')
    );
}

export const ATTACH_URLS_VISIBLE = 4;

export const DETAIL_ATTACH_URL_LINK_STYLE = {
    marginTop: 0,
    textAlign: 'left',
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
};

export function handleAttachmentItemClick(att, setPreviewImageUrl, resolveAttachmentUrl) {
    const raw = att.url ?? att.href ?? att.fileUrl ?? att.path;
    if (!raw) return;
    const url = resolveAttachmentUrl(raw);
    if (isAttachmentImage(att)) {
        setPreviewImageUrl(raw.startsWith('data:') ? raw : url);
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
}

export function handleMarkdownLinkClick({ event, href, rawHref }, projectAttachments, handleAttachmentClick) {
    const h = rawHref || href || '';
    const attachmentMatch = typeof h === 'string' ? h.match(/^#attachment-(\d+)$/) : null;
    if (!attachmentMatch) return;
    event?.preventDefault?.();
    const index = parseInt(attachmentMatch[1], 10);
    
    const items = Array.isArray(projectAttachments) ? projectAttachments : [];
    const filesOnly = items.filter((x) => String(x.type || 'file').toLowerCase() !== 'link');
    const att = filesOnly[index] ?? items[index];
    
    if (att) {
        const normalized = {
            ...att,
            url: att.url ?? att.href ?? att.fileUrl ?? att.path,
            name: att.title ?? att.name,
        };
        handleAttachmentClick(normalized);
    }
}


